import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { EncSeries, Encoding, TextDisplay } from '@mode-vegas/vegas';
import { IconComponent } from '@mode/capra';
import { SelectedChartService } from '@mode/charts/contract';
import { EncodingHelpers, RenderDataModel } from '@mode/charts/util-vegas';
import { BinSizeTypes, ChartTypes, FlamingoTypes } from '@mode/shared/contract-common';
import { DialogService } from '@mode/shared/ui';
import { CompiledField } from '@mode/shared/util-switch';
import { Subject } from 'rxjs';
import { EditTextModalComponent, EditTextModalData } from '../edit-text-modal/edit-text-modal.component';
import { ChannelSummary } from '../marks-helpers';
import { ChannelComponent } from './channel.component';

@Component({
  standalone: true,
  imports: [ChannelComponent, CommonModule, IconComponent],
  selector: 'mode-text-channel-component',
  templateUrl: './text-channel.component.html',
  styleUrls: ['./text-channel.component.scss'],
})
export class TextChannelComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) channelSummary!: ChannelSummary;
  @Input({ required: true }) encoding!: Encoding;
  @Input({ required: true }) model!: FlamingoTypes.ModeModel;
  @Input() renderModel: RenderDataModel | undefined;
  @Input({ required: true }) view!: FlamingoTypes.ModeView;
  @Input({ required: true }) chartService!: SelectedChartService;
  @Input({ required: true }) chartType!: ChartTypes.ChartType;
  @Input() series: EncSeries | false = false;
  @Input({ required: true }) format!: ChartTypes.ChartFormat;
  @Input() queryToken: string | undefined;
  @Input() queryRunToken: string | undefined;
  @Input() vizToken: string | undefined;
  @Input() fieldFormatMap: ChartTypes.FieldFormatMap | undefined;
  @Input() binSizeToFieldMap: BinSizeTypes.BinSizeToFieldMap | undefined;

  private destroyed$ = new Subject<void>();
  isEnabled = false;

  constructor(private dialogService: DialogService) {}

  ngOnChanges(changes: SimpleChanges): void {
    this.isEnabled = this.isTextSettingsAllowed();
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  isTextSettingsAllowed() {
    if (this.encoding.marks.channel === '*' || this.view == null) {
      return false;
    }
    if (this.channelSummary.field == null) {
      return false;
    }
    return true;
  }

  openTextModal() {
    if (!this.isEnabled) {
      return;
    }

    const textField = this.channelSummary.field;
    if (textField == null) {
      return;
    }

    const compiledField = CompiledField.fromEncField(this.view, textField);

    const textDisplay = this.series
      ? this.series.properties.text.display
      : this.encoding.marks.series[0]?.properties.text.display;

    const resolvedTextDisplay = EncodingHelpers.resolveTextDisplay(textDisplay);

    const data: EditTextModalData = {
      showAll: resolvedTextDisplay.showAll,
      showFirst: resolvedTextDisplay.showFirst,
      showLast: resolvedTextDisplay.showLast,
      showMax: resolvedTextDisplay.showMax,
      showMin: resolvedTextDisplay.showMin,
      fieldName: compiledField.displayName ?? '',
      series: this.series ? this.series.field : undefined,
      useLabelThinning: resolvedTextDisplay.useLabelThinning,
      onApply: this.applyTextOptions.bind(this),
    };
    this.dialogService.open({
      component: EditTextModalComponent,
      matDialogConfig: {
        backdropClass: 'mode-viz-backdrop',
        data,
      },
    });
  }

  applyTextOptions(
    isShowAll: boolean,
    isShowFirst: boolean,
    isShowLast: boolean,
    isShowMax: boolean,
    isShowMin: boolean,
    useLabelThinning: boolean,
    series: string | undefined
  ) {
    const textDisplay: TextDisplay = {
      useLabelThinning: useLabelThinning,
      showAll: isShowAll,
      showFirst: isShowFirst,
      showLast: isShowLast,
      showMax: isShowMax,
      showMin: isShowMin,
    };
    const encodingResult = EncodingHelpers.setTextDisplay(this.encoding, textDisplay, series);
    this.chartService.dispatchEncodingUpdate(encodingResult);
  }
}
